import { supabase } from "./supabase";

export type Tenant = {
  id: string;
  slug: string;
  nome_fantasia: string;
  razao_social?: string | null;
  is_admin: boolean;
  logo_url: string | null;
  logo_invert: boolean;
  logo_mode: "image_only" | "image_text" | "text_only";
  logo_colapsado_url?: string | null;
  primary_color: string | null;
  sidebar_bg: string | null;
  sidebar_fg: string | null;
  accent_color: string | null;
  tema_padrao: "light" | "dark" | "auto";
  permite_tema_cliente: boolean;
  plano: string;
  modulos_ativos: string[] | null;
};

const ADMIN_HOSTS = new Set(["aferi.app", "www.aferi.app", "admin.aferi.app", "app.aferi.app"]);
const ROOT_HOSTS = new Set([...ADMIN_HOSTS, "localhost", "127.0.0.1"]);
const CACHE_TTL_MS = 5 * 60_000;

function adminTenant(): Tenant {
  return {
    id: "",
    slug: "admin",
    nome_fantasia: "Aferi",
    razao_social: null,
    is_admin: true,
    logo_url: null,
    logo_invert: true,
    logo_mode: "image_text",
    primary_color: null,
    sidebar_bg: null,
    sidebar_fg: null,
    accent_color: null,
    tema_padrao: "light",
    permite_tema_cliente: true,
    plano: "admin",
    modulos_ativos: null,
  };
}

export async function resolveTenant(): Promise<Tenant> {
  const host = location.hostname.toLowerCase();

  if (ADMIN_HOSTS.has(host)) return adminTenant();

  let slug: string | null = null;
  if (host.endsWith(".aferi.app")) {
    slug = host.replace(/\.aferi\.app$/, "");
  } else if (ROOT_HOSTS.has(host)) {
    slug = new URLSearchParams(location.search).get("tenant");
  }

  if (!slug) return adminTenant();

  // Cache local
  const cacheKey = `tenant:${slug}`;
  try {
    const cached = sessionStorage.getItem(cacheKey);
    if (cached) {
      const { data, ts } = JSON.parse(cached);
      if (Date.now() - ts < CACHE_TTL_MS) return data as Tenant;
    }
  } catch {}

  const { data, error } = await supabase
    .from("tenants")
    .select(`id, slug, nome_fantasia, razao_social, logo_url, logo_invert, logo_mode,
             logo_colapsado_url, primary_color, sidebar_bg, sidebar_fg, accent_color,
             tema_padrao, permite_tema_cliente, plano, modulos_ativos`)
    .eq("slug", slug)
    .maybeSingle();

  if (error || !data) {
    console.warn(`Tenant não encontrado: ${slug}`, error);
    return adminTenant();
  }

  const tenant: Tenant = {
    ...(data as any),
    is_admin: false,
    logo_invert: data.logo_invert ?? true,
    logo_mode: (data.logo_mode as any) ?? "image_text",
    tema_padrao: (data.tema_padrao as any) ?? "light",
    permite_tema_cliente: data.permite_tema_cliente ?? true,
  };

  try {
    sessionStorage.setItem(cacheKey, JSON.stringify({ data: tenant, ts: Date.now() }));
  } catch {}

  return tenant;
}

const TENANT_FIELDS = `id, slug, nome_fantasia, razao_social, logo_url, logo_invert, logo_mode,
  logo_colapsado_url, primary_color, sidebar_bg, sidebar_fg, accent_color,
  tema_padrao, permite_tema_cliente, plano, modulos_ativos`;

/**
 * Resolve um tenant pelo ID (usado após login, quando o usuário já está
 * autenticado e a RLS de `tenants` permite a leitura via membership/super_admin).
 * É o que popula o tenant store em hosts admin, onde o subdomínio não dá o slug.
 */
export async function resolveTenantById(id: string): Promise<Tenant | null> {
  if (!id) return null;
  const { data, error } = await supabase
    .from("tenants").select(TENANT_FIELDS).eq("id", id).maybeSingle();
  if (error || !data) return null;
  return {
    ...(data as any),
    is_admin: false,
    logo_invert: data.logo_invert ?? true,
    logo_mode: (data.logo_mode as any) ?? "image_text",
    tema_padrao: (data.tema_padrao as any) ?? "light",
    permite_tema_cliente: data.permite_tema_cliente ?? true,
  };
}
